import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  /** Server sort key; columns without one are not sortable. */
  sortKey?: string
  className?: string
  cell: (row: T) => ReactNode
}

/** Row selection: a checkbox column, with "all on this page" in the header. */
export interface DataTableSelection<T> {
  /** The `rowKey`s of the selected rows. */
  selected: ReadonlySet<string>
  onToggle: (key: string) => void
  onToggleAll: () => void
  /** The name of a row, for the accessible name of its checkbox. */
  label: (row: T) => string
}

/**
 * Table for the CRM lists (design §4.0, C §2): sortable headers with `aria-sort`, quiet
 * refresh (rows stay visible at 60% while a new page loads), an optional extra row after
 * each row (expandable contact persons), row selection and a header that stays in view.
 * `sort` is a key, or the key with a leading '-' for descending.
 *
 * A sticky header needs its own scroll area from 768 px: the `overflow-x-auto` wrapper of
 * `Table` would take the sticking away, so `stickyHeader` renders a plain table in one.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort,
  onSortChange,
  refreshing = false,
  empty,
  renderAfterRow,
  caption,
  selection,
  stickyHeader = false,
}: {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  sort?: string
  onSortChange?: (sort: string) => void
  refreshing?: boolean
  empty?: ReactNode
  renderAfterRow?: (row: T) => ReactNode
  caption?: string
  selection?: DataTableSelection<T>
  stickyHeader?: boolean
}) {
  if (rows.length === 0 && empty) return <>{empty}</>
  const sortKey = sort?.replace(/^-/, '')
  const descending = sort?.startsWith('-') ?? false
  const selectedCount = selection ? rows.filter((row) => selection.selected.has(rowKey(row))).length : 0
  const allSelected = rows.length > 0 && selectedCount === rows.length
  const content = (
    <>
      {caption ? <caption className="sr-only">{caption}</caption> : null}
      <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-card')}>
        <TableRow>
          {selection ? (
            <TableHead className="w-10">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={allSelected}
                // "Some" has no attribute: it is a property of the element.
                ref={(element) => {
                  if (element) element.indeterminate = selectedCount > 0 && !allSelected
                }}
                onChange={selection.onToggleAll}
                aria-label="Selecteer alles op deze pagina"
              />
            </TableHead>
          ) : null}
          {columns.map((column) => {
            const key = column.sortKey
            const active = key !== undefined && key === sortKey
            const direction = active ? (descending ? 'descending' : 'ascending') : 'none'
            return (
              <TableHead
                key={column.key}
                className={column.className}
                aria-sort={key ? direction : undefined}
              >
                {key && onSortChange ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                    onClick={() => onSortChange(active && !descending ? `-${key}` : key)}
                  >
                    {column.header}
                    {direction === 'ascending' ? (
                      <ArrowUp className="size-3.5" aria-hidden />
                    ) : direction === 'descending' ? (
                      <ArrowDown className="size-3.5" aria-hidden />
                    ) : (
                      <ArrowUpDown className="size-3.5 opacity-40" aria-hidden />
                    )}
                  </button>
                ) : (
                  column.header
                )}
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const key = rowKey(row)
          const selected = selection?.selected.has(key) ?? false
          return (
            <Fragment key={key}>
              <TableRow data-state={selected ? 'selected' : undefined}>
                {selection ? (
                  <TableCell className="w-10">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={selected}
                      onChange={() => selection.onToggle(key)}
                      aria-label={`Selecteer ${selection.label(row)}`}
                    />
                  </TableCell>
                ) : null}
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
              {renderAfterRow?.(row)}
            </Fragment>
          )
        })}
      </TableBody>
    </>
  )
  return (
    <div
      className={cn('transition-opacity', refreshing && 'opacity-60')}
      aria-busy={refreshing || undefined}
    >
      {stickyHeader ? (
        <div className="overflow-auto md:max-h-[calc(100svh-16rem)]">
          <table data-slot="table" className="w-full caption-bottom text-sm">
            {content}
          </table>
        </div>
      ) : (
        <Table>{content}</Table>
      )}
    </div>
  )
}
